import { QuestionnaireTemplate } from './QuestionnaireContext';

// Field interfaces matching the ones used in QuestionnaireForm
interface BaseField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  helpText?: string;
  conditional?: (formData: any) => boolean;
}

interface TextField extends BaseField {
  type: 'text' | 'email' | 'tel' | 'url' | 'date';
  placeholder: string;
}

interface TextareaField extends BaseField {
  type: 'textarea';
  placeholder: string;
}

interface SelectField extends BaseField {
  type: 'select';
  options: { value: string; label: string }[];
}

interface RadioField extends BaseField {
  type: 'radio';
  options: { value: string; label: string }[];
}

interface CheckboxField extends BaseField {
  type: 'checkbox';
  options: { value: string; label: string }[];
}

interface FileField extends BaseField {
  type: 'file';
  accept?: string;
}

interface RepeaterField extends BaseField {
  type: 'repeater';
}

interface RangeField extends BaseField {
  type: 'range';
  min?: number;
  max?: number;
}

type Field = TextField | TextareaField | SelectField | RadioField | CheckboxField | FileField | RepeaterField | RangeField;

// Step interface matching the one used in QuestionnaireForm
interface Step {
  title: string;
  description: string;
  fields: Field[];
  conditional?: (formData: any) => boolean;
}

/**
 * Converts a questionnaire template to the Step format used by QuestionnaireForm
 */
export const convertTemplateToSteps = (template: QuestionnaireTemplate, formData?: any): Step[] => {
  if (!template.sections || template.sections.length === 0) {
    return [];
  }

  // Sort sections by order
  const sortedSections = [...template.sections].sort((a, b) => a.order - b.order);

  // Filter sections based on conditional rules
  const visibleSections = sortedSections.filter(section => {
    if (!section.conditionalRules || section.conditionalRules.length === 0) {
      return true; // Always show sections without conditional rules
    }

    // Evaluate conditional rules for this section
    return evaluateConditionalRules(section.conditionalRules, formData || {});
  });

  // Convert sections to steps, handling both direct questions and subsections
  const steps: Step[] = [];
  
  visibleSections.forEach(section => {
    // All questions are now within subsections
    if (section.subsections && section.subsections.length > 0) {
      // Filter subsections based on conditional rules
      const visibleSubsections = section.subsections.filter(subsection => {
        if (!subsection.conditionalRules || subsection.conditionalRules.length === 0) {
          return true; // Always show subsections without conditional rules
        }
        
        // Evaluate conditional rules for this subsection
        return evaluateConditionalRules(subsection.conditionalRules, formData || {});
      });
      
      // Only create steps for visible subsections
      if (visibleSubsections.length > 0) {
        // If section has only one visible subsection, use the section's title and description
        if (visibleSubsections.length === 1) {
          const subsection = visibleSubsections[0];
          // Filter questions based on conditional rules
          const visibleQuestions = subsection.questions.filter(question => {
            if (!question.conditionalRules || question.conditionalRules.length === 0) {
              return true; // Always show questions without conditional rules
            }
            return evaluateConditionalRules(question.conditionalRules, formData || {});
          });
          
          steps.push({
            title: section.title, // Use section title instead of subsection title
            description: section.description || '', // Use section description or empty string
            fields: visibleQuestions.map((question: any) => createFieldFromQuestion(question))
          });
        } else {
          // If section has multiple visible subsections, create a step for each subsection
          visibleSubsections.forEach(subsection => {
            // Filter questions based on conditional rules
            const visibleQuestions = subsection.questions.filter(question => {
              if (!question.conditionalRules || question.conditionalRules.length === 0) {
                return true; // Always show questions without conditional rules
              }
              return evaluateConditionalRules(question.conditionalRules, formData || {});
            });
            
            steps.push({
              title: subsection.title,
              description: subsection.description || '',
              fields: visibleQuestions.map((question: any) => createFieldFromQuestion(question))
            });
          });
        }
      }
    }
  });

  return steps;
};

/**
 * Creates a Field object from a template question
 */
const createFieldFromQuestion = (question: any): Field => {
  const baseField = {
    name: question.id,
    label: question.title,
    required: question.required || false,
    helpText: question.description
  };

  const mappedType = mapQuestionTypeToFieldType(question.type);

  switch (mappedType) {
    case 'text':
    case 'email':
    case 'url':
    case 'date':
      return {
        ...baseField,
        type: mappedType,
        placeholder: question.placeholder || ''
      } as TextField;

    case 'textarea':
      return {
        ...baseField,
        type: 'textarea',
        placeholder: question.placeholder || ''
      } as TextareaField;

    case 'select':
      return {
        ...baseField,
        type: 'select',
        options: question.options?.map((option: any) => ({
          value: option.value,
          label: option.label
        })) || []
      } as SelectField;

    case 'radio':
      // Special handling for boolean questions - auto-generate Yes/No options if none provided
      if (question.type === 'boolean') {
        const hasCustomOptions = question.options && question.options.length > 0;
        return {
          ...baseField,
          type: 'radio',
          options: hasCustomOptions 
            ? question.options.map((option: any) => ({
                value: option.value,
                label: option.label
              }))
            : [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]
        } as RadioField;
      }
      
      return {
        ...baseField,
        type: 'radio',
        options: question.options?.map((option: any) => ({
          value: option.value,
          label: option.label
        })) || []
      } as RadioField;

    case 'checkbox':
      return {
        ...baseField,
        type: 'checkbox',
        options: question.options?.map((option: any) => ({
          value: option.value,
          label: option.label
        })) || []
      } as CheckboxField;

    case 'file':
      return {
        ...baseField,
        type: 'file',
        accept: question.accept || '*/*'
      } as FileField;

    case 'repeater':
      return {
        ...baseField,
        type: 'repeater'
      } as RepeaterField;

    case 'range':
      return {
        ...baseField,
        type: 'range',
        min: question.min,
        max: question.max
      } as RangeField;

    default:
      // Default to text field
      return {
        ...baseField,
        type: 'text',
        placeholder: question.placeholder || ''
      } as TextField;
  }
};

/**
 * Evaluates conditional rules to determine if a section should be shown
 */
export const evaluateConditionalRules = (conditionalRules: any[], formData: any): boolean => {
  // For now, we'll implement basic logic where all rules must be true (AND logic)
  // This can be extended later to support OR logic and more complex conditions
  
  return conditionalRules.every(rule => {
    if (rule.type !== 'show') {
      return true; // Skip non-show rules for now
    }

    return rule.conditions.every((condition: any) => {
      const questionValue = formData[condition.questionId];
      
      switch (condition.operator) {
        case 'contains':
          if (Array.isArray(questionValue)) {
            return questionValue.includes(condition.value);
          }
          return false;
          
        case 'equals':
          return questionValue === condition.value;
          
        case 'not_equals':
          return questionValue !== condition.value;
          
        case 'in':
          if (Array.isArray(condition.value)) {
            return condition.value.includes(questionValue);
          }
          return false;
          
        case 'not_in':
          if (Array.isArray(condition.value)) {
            return !condition.value.includes(questionValue);
          }
          return true;
          
        default:
          console.warn(`Unknown conditional operator: ${condition.operator}`);
          return true;
      }
    });
  });
};

/**
 * Maps template question types to form field types
 */
const mapQuestionTypeToFieldType = (questionType: string): string => {
  const typeMap: Record<string, string> = {
    'text': 'text',
    'textarea': 'textarea',
    'email': 'email',
    'url': 'url',
    'number': 'number',
    'date': 'date',
    'radio': 'radio',
    'checkbox': 'checkbox', // Keep checkbox as checkbox
    'select': 'select',
    'multiselect': 'multiselect',
    'file': 'file',
    'range': 'range',
    'boolean': 'radio', // Map boolean to radio with yes/no options
    'repeater': 'repeater'
  };

  return typeMap[questionType] || 'text';
};